import { ROUTES } from "@/app/constants/routes_sidebar/routes.constant";

const RESERVED_SUBDOMAINS = new Set(["admin", "customer", "www", "workplanx"]);

const PUBLIC_CONSENT_PATHS = [
  ROUTES.LOGIN,
  ROUTES.MAGIC_LOGIN,
  ROUTES.NO_PERMISSION,
  ROUTES.THANK_YOU,
  ROUTES.TIMESHEET_APPROVAL,
  ROUTES.DOCKET_SIGNATURE,
];

type CookieConsentLocation = {
  hostname: string;
  pathname: string;
};

const normalizePathname = (pathname: string): string => {
  if (!pathname) {
    return ROUTES.ROOT;
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
};

const pathMatchesRoute = (pathname: string, route: string): boolean => {
  return pathname === route || pathname.startsWith(`${route}/`);
};

/**
 * Cookie consent belongs only to business workspace subdomains.
 * Main/home, admin, customer portal, and bare localhost domains are outside scope.
 * @param {string} hostname Hostname to evaluate.
 * @returns {boolean} True if hostname is a workspace subdomain, false otherwise.
 */
export const isCookieConsentWorkspaceHost = (hostname: string): boolean => {
  const normalizedHostname = hostname.trim().toLowerCase();

  if (
    !normalizedHostname ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(normalizedHostname)
  ) {
    return false;
  }

  const parts = normalizedHostname.split(".").filter(Boolean);
  const [subdomain = ""] = parts;

  if (RESERVED_SUBDOMAINS.has(subdomain)) {
    return false;
  }

  if (normalizedHostname.endsWith(".localhost")) {
    return parts.length >= 2;
  }

  return parts.length >= 3;
};

export const isCookieConsentPublicPath = (pathname: string): boolean => {
  const normalizedPathname = normalizePathname(pathname);

  return PUBLIC_CONSENT_PATHS.some((route) =>
    pathMatchesRoute(normalizedPathname, route),
  );
};

export const isCookieConsentEnabledForLocation = ({
  hostname,
  pathname,
}: CookieConsentLocation): boolean => {
  return (
    isCookieConsentWorkspaceHost(hostname) &&
    !isCookieConsentPublicPath(pathname)
  );
};
